import HtmlPushpin from './HtmlPushpin';
import { addEventsToLayer, setMap } from './HtmlPushpinLayerEvents';

// #genai - Helper function extracted to reduce nesting depth
const createPushpin = (mapPoint, index, options) => new HtmlPushpin(index, mapPoint, {
  propertyType: options.propertyType,
  isComplex: options.isComplex,
  handleDeletePin: options.handleDeletePin,
  handleFocusChange: () => options.handleFocusChange(index),
  focusedPinIndex: options.focusedPinIndex,
  pinDragEnabled: options.pinDragEnabled,
  colors: options.colors,
  handleFetchPinAddress: options.handleFetchPinAddress,
});

export default (handleAddPin, handleUpdatePinLocation, handleRemoveFocus) => {
  const { Microsoft } = window;

  // #genai - Refactored: methods moved to prototype to reduce nesting
  /** A reusable class for overlaying HTML elements as pushpins on the map. */
  function HtmlPushpinLayer() {
    /** Store the pushpins. */
    this.pushpins = [];

    /** A variable to store a reference to the container for the HTML pushpins. */
    this.container = null;

    this.isComplex = false;
  }

  // Define a custom overlay class that inherits from the CustomOverlay class.
  HtmlPushpinLayer.prototype = new Microsoft.Maps.CustomOverlay({ beneathLabels: false });

  // #genai - Getter/setter methods moved to prototype
  HtmlPushpinLayer.prototype.setIsComplex = function setIsComplex(value) {
    this.isComplex = value;
  };

  HtmlPushpinLayer.prototype.getIsComplex = function getIsComplex() {
    return this.isComplex;
  };

  // #genai - Refactored to use options object to reduce parameter count
  /** Method to define the pushpins to display in the layer. */
  HtmlPushpinLayer.prototype.setPushpins = function setPushpins(mapPoints, options) {
    const {
      propertyType,
      handleDeletePin,
      focusedPinIndex,
      pinDragEnabled,
      colors,
      handleFocusChange,
      handleFetchPinAddress,
    } = options;

    this.pushpins = [];

    // #genai - Early return to reduce nesting
    if (mapPoints.length === 0) {
      this.addPushpins();
      return;
    }

    const pushpinOptions = {
      propertyType,
      isComplex: this.isComplex,
      handleDeletePin,
      handleFocusChange,
      focusedPinIndex,
      pinDragEnabled,
      colors,
      handleFetchPinAddress,
    };

    // #genai - Use map instead of forEach to reduce nesting
    this.pushpins = mapPoints.map((mapPoint, index) => createPushpin(mapPoint, index, pushpinOptions));

    // Move the focused pin to end of array to show above other pins.
    this.pushpins.push(this.pushpins.splice(focusedPinIndex, 1)[0]);

    this.addPushpins();
  };

  HtmlPushpinLayer.prototype.addPushpins = function addPushpins() {
    // #genai - Early return to reduce nesting
    if (!this.container) {
      return;
    }

    // Clear the container.
    this.container.innerHTML = '';

    this.pushpins.forEach((newPin) => this.container.appendChild(newPin.element));
    this.updatePositions();
  };

  /** A function that updates the position of a HTML pushpin element on the map. */
  HtmlPushpinLayer.prototype.updatePushpinPosition = function updatePushpinPosition(pin) {
    const map = this.getMap();
    // #genai - Early return to reduce nesting
    if (!map) {
      return;
    }

    // Calculate the pixel location of the pushpin.
    const { element, location: { latitude, longitude } } = pin;
    const topLeft = map.tryLocationToPixel(
      new Microsoft.Maps.Location(latitude, longitude),
      Microsoft.Maps.PixelReference.control,
    );

    // Update the position of the pushpin element.
    element.style.left = `${topLeft.x}px`;
    element.style.top = `${topLeft.y}px`;
  };

  /** A function that updates the positions of all HTML pushpins in the layer. */
  HtmlPushpinLayer.prototype.updatePositions = function updatePositions() {
    this.pushpins.forEach((pushpin) => this.updatePushpinPosition(pushpin));
  };

  /**  Implement the onAdd method to set up DOM elements, and use setHtmlElement to bind it with the overlay. */
  HtmlPushpinLayer.prototype.onAdd = function onAdd() {
    // Create a div that will hold the pushpins.
    this.container = document.createElement('div');
    this.container.style.position = 'absolute';
    this.container.style.left = '0px';
    this.container.style.top = '0px';
    this.container.style.width = '100%';
    this.container.style.height = '100%';
    this.container.style.zIndex = 'unset';
    this.container.setAttribute('id', 'HtmlPushpinLayer');

    addEventsToLayer(this, handleAddPin, handleUpdatePinLocation, handleRemoveFocus);

    this.setHtmlElement(this.container);
    // Add event to update pin position when screen size is changed.
    window.onresize = () => this.updatePositions();
  };

  HtmlPushpinLayer.prototype.onLoad = function onLoad() {
    setMap(this.getMap());
    this.addPushpins();
  };

  return HtmlPushpinLayer;
};
